<template>
  <div class="backgroundColor pa-6" style="min-height: 100vh;">
    <header class="mb-6 d-flex align-center">
      <v-tooltip bottom>
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            text
            color="grey darken-1"
            class="px-0 mr-4"
            @click="volverBandeja"
            v-bind="attrs"
            v-on="on"
          >
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>
        </template>
        <span>Volver a la Gestión de Entregas</span>
      </v-tooltip>
      <h1 class="text-h4 font-weight-bold primary--text mb-0 mr-4">
        Historial Entregas Venta: <span class="font-weight-regular">{{ folioVenta }}</span>
      </h1>
      <v-chip small dark :color="macroChipColor" class="font-weight-medium">
        {{ orderData ? orderData.macro_status_description : 'Cargando...' }}
      </v-chip>
    </header>
    <v-row v-if="isLoading" justify="center" align="center" style="min-height: 50vh;">
      <v-col class="text-center" cols="12">
        <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
        <div class="mt-4 grey--text text--darken-1 text-body-2">
          Compilando bitácora y saldos del resguardo...</div>
      </v-col>
    </v-row>
    <v-row v-else-if="orderData">
      <v-col cols="12" md="8">
        <v-card outlined class="pa-5 mb-6">
          <div class="d-flex justify-space-between align-center mb-2">
            <h2 class="text-h6 font-weight-bold primary--text mb-0">
              <span>Balance Consolidado de Artículos</span>
            </h2>
            <v-btn
              v-if="orderData.macro_status_id !== 3"
              small
              outlined
              class="ml-2"
              @click="openDistributionModal"
            >
              Reasignar
            </v-btn>
          </div>
          <p class="grey--text text--darken-1 mb-4 text-body-2">
            Estado y saldo acumulado neto de los productos comprados sin stock en esta orden.
          </p>
          <v-simple-table height="300px" class="elevation-0 compact-header-table">
            <template v-slot:default>
              <thead class="grey lighten-4">
                <tr>
                  <th rowspan="2" class="text-left text-uppercase font-weight-bold grey--text text--darken-2 header-align-bottom">
                    Producto</th>
                  <th rowspan="2" class="text-center text-uppercase font-weight-bold grey--text text--darken-2 header-align-bottom">
                    Comprado</th>
                  <th
                    :colspan="pickupStores.length"
                    class="text-center text-uppercase font-weight-bold grey--text text--darken-2">
                    Cantidad Entregada Por Sucursal
                  </th>
                  <th rowspan="2" class="text-center text-uppercase font-weight-bold grey--text text--darken-2 header-align-bottom">
                    Saldo Actual</th>
                </tr>
                <tr>
                  <th
                    v-for="(store, index) in pickupStores"
                    :key="index"
                    class="text-center text-uppercase font-weight-bold grey--text text--darken-2">
                    {{ store.name }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(prod, index) in balanceProducts"
                  :key="index"
                  :class="{'green lighten-5': prod.current_balance === 0}"
                  class="transition-swing"
                >
                  <td class="py-3" style="max-width: 250px;">
                    <div class="text-truncate font-weight-bold text-body-2
                      grey--text text--darken-4" :title="prod.name">
                      {{ prod.name }}
                    </div>
                    <div class="text-truncate text-caption grey--text text--darken-1 mt-1">
                      <span class="font-weight-bold grey--text text--darken-3">{{ prod.sku }}</span>
                      <span class="mx-1">&bull;</span> {{ prod.brand }}
                      <span class="mx-1">&bull;</span> {{ prod.model }}
                    </div>
                  </td>
                  <td class="text-center text-body-2 grey--text text--darken-3">
                    {{ prod.original_ordered }} pz
                  </td>
                  <td
                    v-for="(store, index) in pickupStores"
                    :key="index"
                    class="text-center text-body-2 grey--text text--darken-3">
                    {{ prod[`storeId${store.id}_total_delivered`] || 0 }} pz
                  </td>
                  <td class="text-center text-body-2 font-weight-black"
                    :class="prod.current_balance === 0 ? 'success--text'
                    : 'grey--text text--darken-4'"
                  >
                    {{ prod.current_balance }} pz
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </v-card>
        <v-card outlined class="pa-5">
          <div class="d-flex align-center mb-4">
            <h2 class="text-h6 font-weight-bold primary--text mb-0">
              Línea de Tiempo de Entregas de Producto
            </h2>
          </div>
          <v-card-text v-if="!timelineItems
            || timelineItems.length === 0"
            class="text-center py-8 grey--text">
            No se registran visitas ni entregas físicas para este folio todavía.
          </v-card-text>
          <v-timeline v-else dense clipped class="custom-delivery-timeline">
            <v-timeline-item
              v-for="(item, index) in timelineItems"
              :key="index"
              :color="item.type === 'dispatch' ? 'secondary' : 'warning'"
              small
              fill-dot
              :icon="item.type === 'dispatch' ? 'mdi-hand-coin' : 'mdi-package-variant-closed'"
            >
              <DispatchItem v-if="item.type === 'dispatch'" :item="item" />
              <HistoryItem v-else-if="item.type === 'delivery'" :item="item" />
            </v-timeline-item>
          </v-timeline>
        </v-card>
      </v-col>
      <v-col cols="12" md="4">
        <v-card outlined class="pa-5 mb-4">
          <div class="d-flex align-center mb-4">
            <h2 class="text-h6 font-weight-bold primary--text mb-0">Responsable Autorizado</h2>
          </div>
          <v-card color="grey lighten-4" elevation="0" class="pa-3 mb-4">
            <div class="text-body-2 mb-2">
              <span class="font-weight-bold grey--text text--darken-2">Cliente:</span>
                {{ orderData.customer.name }}
            </div>
            <v-divider class="my-2"></v-divider>
            <div class="text-body-2 mb-1">
              <span class="font-weight-bold grey--text text--darken-2">Teléfono:</span>
              {{ orderData.customer.phone || 'No Registrado' }}
            </div>
            <div class="text-body-2">
              <span class="font-weight-bold grey--text text--darken-2">Email:</span>
                {{ orderData.customer.email || 'Sin Correo' }}
            </div>
          </v-card>
          <v-divider class="my-4"></v-divider>
          <div class="text-caption grey--text text--darken-1">
            <div class="mb-1"><strong>Tienda de Compra:</strong>
              {{ orderData.purchase_store_name }}</div>
          </div>
        </v-card>
        <div class="d-flex flex-column gap-3">
          <v-btn
            v-if="orderData.macro_status_id === 2"
            color="secondary"
            block
            large
            elevation="2"
            class="mb-3 font-weight-bold"
            @click="continuarDespachoActivo"
          >
            <v-icon left>mdi-dolly</v-icon>
            Continuar Despacho
          </v-btn>
          <v-btn
            outlined
            color="primary"
            block
            large
            class="mb-3 text-none font-weight-medium bg-white"
            @click="reimprimirValeRecoleccion"
          >
            <v-icon left>mdi-printer</v-icon>
            Reimprimir Vale de Resguardo
          </v-btn>
        </div>
      </v-col>
    </v-row>
    <v-dialog
      v-model="distributionDialog"
      persistent
      max-width="1200px"
      transition="dialog-bottom-transition"
      eager
    >
      <v-card>
        <v-card-title>
          Reasignacion de mercancia
        </v-card-title>
        <v-card-text>
          <v-text-field
            v-model="distributionNotes"
            dense
            outlined
            label="Notas *"
            class="mt-2">
          </v-text-field>
          <DeliveryDistributionTable
            ref="deliveryDistributionTable"
            :sale-folio="folioVentaNumber"
            :pickup-store-id="storeId"
          />
        </v-card-text>
        <v-card-actions class="d-flex justify-space-between">
          <v-btn
            small
            outlined
            color="error"
            @click="hideDistributionDialog"
          >Cancelar</v-btn>
          <v-btn small outlined @click="saveDistributionChanges">
            Guardar cambios
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import Swal from 'sweetalert2';
import { mapGetters } from 'vuex';
import ClientDeliveryService from '@/services/ClientDeliveryService';
import InventoryService from '@/services/InventoryService';
import DispatchItem from './TimelineItems/DispatchItem.vue';
import HistoryItem from './TimelineItems/HistoryItem.vue';
import DeliveryDistributionTable from './DeliveryDistributionTable.vue';

export default {
  name: 'DeliveryHistoryDetail',
  components: {
    DispatchItem,
    HistoryItem,
    DeliveryDistributionTable,
  },
  data() {
    return {
      storeId: null,
      isLoading: true,
      orderData: null,
      distributionDialog: false,
      distributionNotes: '',
    };
  },
  computed: {
    ...mapGetters({
      stores: 'stores',
    }),
    folioVenta() {
      return this.$route.params.folio || 'N/A';
    },
    folioVentaNumber() {
      return Number(this.$route.params.folio);
    },
    macroChipColor() {
      if (!this.orderData) return 'grey';
      // Mapeo exacto del backend (2: Parcial -> warning, 3: Entregado -> success)
      return this.orderData.macro_status_id === 3 ? 'success' : 'orange darken-2';
    },
    pickupStores() {
      return this.stores.filter((item) => !item.isWarehouse);
    },
    balanceProducts() {
      if (this.orderData === null || this.orderData === undefined) return [];
      return this.orderData.balance_products.reduce((acc, item) => {
        if (!acc[item.product_id]) {
          acc[item.product_id] = {
            product_id: item.product_id,
            name: item.name,
            brand: item.brand,
            model: item.model,
            sku: item.sku,
          };
        }
        acc[item.product_id].original_ordered = (acc[item.product_id].original_ordered || 0) + (item.original_ordered || 0);
        acc[item.product_id][`storeId${item.pickup_store_id}_total_delivered`] = (acc[item.product_id][`storeId${item.pickup_store_id}_total_delivered`] || 0) + (item.total_delivered || 0);
        acc[item.product_id].current_balance = (acc[item.product_id].current_balance || 0) + (item.current_balance || 0);
        return acc;
      }, {});
    },
    timelineItems() {
      const dispatches = [];
      if (this.orderData.timeline_dispatches) {
        dispatches.push(...this.orderData.timeline_dispatches.map((item) => ({
          ...item,
          type: 'dispatch',
        })));
      }
      const deliveryHistory = [];
      if (this.orderData.delivery_history) {
        deliveryHistory.push(...this.orderData.delivery_history.map((item) => ({
          ...item,
          type: 'delivery',
        })));
      }
      return [...dispatches, ...deliveryHistory].sort((a, b) => new Date(a.date) - new Date(b.date));
    },
  },
  async mounted() {
    await this.fetchHistoryDetails();
  },
  methods: {
    buildDistributionArray() {
      const groupedProducts = this.orderData.balance_products.reduce((acc, item) => {
        if (!acc[item.product_id]) {
          acc[item.product_id] = {
            id: item.product_id,
            barcode: item.sku,
            name: item.name,
            backorder: 0,
          };
        }
        acc[item.product_id].backorder += item.current_balance || 0;
        acc[item.product_id][`storeId${item.pickup_store_id}`] = item.current_balance || 0;
        return acc;
      }, {});
      return Object.values(groupedProducts);
    },
    openDistributionModal() {
      const products = this.buildDistributionArray();
      this.$refs.deliveryDistributionTable.initializePickupDistribution(products);
      this.distributionDialog = true;
    },
    hideDistributionDialog() {
      this.distributionNotes = '';
      this.distributionDialog = false;
    },
    buildProductArray() {
      const products = [];
      this.orderData.balance_products.forEach((item) => {
        for (let i = 0; i < item.current_balance; i += 1) {
          products.push({
            productId: item.product_id,
          });
        }
      });
      return products;
    },
    async saveDistributionChanges() {
      const products = this.buildProductArray();
      const { products: assignedProducts, requiresConfirmation } = this.$refs.deliveryDistributionTable.getAssignedData(products);
      if (requiresConfirmation) {
        const result = await Swal.fire({
          icon: 'warning',
          title: 'Confirmar distribución',
          text: 'Stock local insuficiente para algunos de los productos. Al confirmar, la mercancía se solicitará a Almacén Central y el cliente deberá esperar la llegada del traspaso. ¿Desea continuar?',
          showCancelButton: true,
          confirmButtonColor: 'primary',
          cancelButtonColor: 'secondary',
          cancelButtonText: 'Cancelar',
        });
        if (!result.isConfirmed) {
          return;
        }
      }
      try {
        await InventoryService.saveSaleProductsDistribution(
          this.folioVentaNumber,
          this.storeId,
          assignedProducts,
          this.distributionNotes,
        );
        Swal.fire({
          icon: 'success',
          title: 'Distribución guardada',
          text: 'La distribución de productos se ha guardado correctamente.',
          confirmButtonColor: 'primary',
        });
        this.distributionNotes = '';
        await this.fetchHistoryDetails();
        this.hideDistributionDialog();
      } catch (error) {
        console.error('Error saving distribution:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error al guardar la distribución',
          text: error,
          confirmButtonColor: 'primary',
        });
      }
      this.distributionDialog = false;
    },
    async fetchHistoryDetails() {
      this.isLoading = true;
      try {
        const { folio } = this.$route.params;
        const storeId = this.$store.getters.currentStoreId;
        this.storeId = storeId;

        const response = await ClientDeliveryService.getHistoryOrderDetails(folio, storeId);

        if (response.status === 'success') {
          this.orderData = response.data;
        }
      } catch (error) {
        Swal.fire({
          icon: 'error',
          title: 'Historial no disponible',
          text: error.response?.data?.detail || 'Ocurrió un problema al leer la bitácora física de este folio.',
          confirmButtonColor: 'primary',
        }).then(() => {
          this.volverBandeja();
        });
      } finally {
        this.isLoading = false;
      }
    },
    continuarDespachoActivo() {
      // Redirecciona al operador directo a la pantalla de steppers operativos pasándole el folio
      this.$router.push({
        name: 'order-dispatch',
        params: { folio: this.folioVenta },
      });
    },
    reimprimirValeRecoleccion() {
      const routeData = this.$router.resolve({
        name: 'resguardo-vale-ticket',
        // Asegúrate de enviar los mismos parámetros que definiste en tu router
        params: { folio: String(this.folioVenta), mode: 'todas' },
      });

      // Intentamos abrir la pestaña
      const openedTab = window.open(routeData.href, '_blank', 'noopener,noreferrer');

      // Si el navegador tiene un bloqueador de ventanas emergentes muy agresivo,
      // redirigimos en la misma pestaña como plan B.
      if (!openedTab) {
        this.$router.push({
          name: 'resguardo-vale-ticket',
          params: { folio: String(this.folioVenta), mode: 'todas' },
        });
      }
    },
    volverBandeja() {
      this.$router.back();
    },
  },
};
</script>

<style scoped>
.gap-3 {
  gap: 12px;
}
.border-orange-light {
  border: 1px solid #ffe0b2 !important;
}
/* Estilo estilizado para la línea de tiempo de auditoría */
.custom-delivery-timeline >>> .v-timeline-item__body {
  max-width: 100%;
}
.compact-header-table >>> thead th {
  height: auto !important;
  padding: 4px 8px !important;
  line-height: 1.1;
}
.compact-header-table >>> thead tr:first-child th {
  padding-top: 6px !important;
}
.compact-header-table >>> thead tr:last-child th {
  padding-top: 0 !important;
  padding-bottom: 6px !important;
}
.compact-header-table >>> thead th.header-align-bottom {
  vertical-align: bottom !important;
  padding-bottom: 6px !important;
}
</style>
